import React, { forwardRef, KeyboardEvent, useContext } from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputContentSizeChangeEventData,
  TextInputKeyPressEventData,
  View,
} from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { IS_WEB, SUPPORTS_FIELD_SIZING } from '../../constants'
import { AppContext } from '../../context'
import { getFontStyles } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { deviceIsSamsung, tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'

const LabelContainer = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: 8,
  fontWeight: 'bold',
  color: theme.colors.textPrimary,
}))

/**
 * Standard input field component allows inputValue to be passed in
 * and calls onInputChange callback if value changes
 *
 * TODO: Consolidate this field with `ui-core-crossplatform/atoms/formElements/InputField.tsx`
 */
export const BaseInputField = forwardRef<TextInput, BaseInputFieldProps>(
  (
    {
      label,
      onInputChange,
      placeholderText,
      inputValue,
      height,
      minHeight,
      maxHeight,
      borderColor,
      borderWidth,
      onContentSizeChange,
      multiline = true,
      scrollEnabled = false,
      width = '80%',
      handleKeyPress,
      handleKeyUp,
      outlineColor,
      outlineStyle,
      onFocus,
      onBlur,
      selection,
    },
    ref,
  ) => {
    const { copyPasteDisabled } = useContext(AppContext)
    const isSamsung = deviceIsSamsung()
    const { colors, fontFamilyMetadata } = useTheme()

    return (
      <View style={{ width }}>
        {label !== undefined ? <LabelContainer>{label}</LabelContainer> : null}
        <TextInput
          style={{
            borderColor: borderColor || colors.inputOutlineDefault,
            borderWidth,
            height,
            minHeight,
            maxHeight,
            alignItems: 'center',
            paddingLeft: 4,
            paddingBottom: Platform.OS === 'ios' ? 8 : 0,
            ...(IS_WEB && { outlineColor: outlineColor || colors.iconActive }),
            // RN-web's TextStyle type omits 'none' for outlineStyle even though it's valid at runtime
            // (ChatInput passes 'none'); cast to the accepted subset so the strict style type compiles.
            ...(IS_WEB && outlineStyle && { outlineStyle: outlineStyle as 'solid' | 'dotted' | 'dashed' }),
            // Web only property to allow text area to shrink and grow with content.
            ...(SUPPORTS_FIELD_SIZING && multiline && { fieldSizing: 'content' }),
            ...getFontStyles(colors, fontFamilyMetadata).body.default,
            lineHeight: 24,
          }}
          ref={ref}
          onKeyPress={(event) => handleKeyPress && handleKeyPress(event)}
          onKeyUp={(event) => handleKeyUp && handleKeyUp(event)}
          testID={tID('InputField')}
          onContentSizeChange={onContentSizeChange}
          onChangeText={(text) => {
            onInputChange(text)
          }}
          multiline={multiline}
          scrollEnabled={scrollEnabled}
          value={inputValue}
          selectionColor={colors.backgroundActive}
          placeholder={placeholderText}
          contextMenuHidden={copyPasteDisabled}
          // See InputField.tsx for a description of this device specific logic
          caretHidden={copyPasteDisabled && isSamsung}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholderTextColor={colors.inputTextPlaceholder}
          selection={selection}
        />
      </View>
    )
  },
)

type BaseInputFieldProps = {
  label?: string
  borderColor?: string
  height?: number
  minHeight?: number
  maxHeight?: number
  scrollEnabled?: boolean
  onInputChange: (text: string) => void
  placeholderText?: string
  inputValue?: string
  multiline?: boolean
  onContentSizeChange?: (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => void
  width?: string | number
  borderWidth?: number
  handleKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void
  handleKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void
  outlineColor?: string
  outlineStyle?: 'solid' | 'dotted' | 'dashed' | 'none'
  onFocus?: () => void
  onBlur?: () => void
  selection?: { start: number; end: number }
}
